import { useEffect, useRef, useState } from 'react';
import { useHotkeysContext } from 'react-hotkeys-hook';

import { HOTKEY_SCOPES } from '@/hooks/hotkeys/scopes';

// Web Speech API types (not included in TypeScript's lib.dom.d.ts)
interface SpeechRecognitionResultList {
  readonly length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  readonly length: number;
  readonly isFinal: boolean;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionAlternative {
  readonly transcript: string;
  readonly confidence: number;
}

interface SpeechRecognitionEvent extends Event {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultList;
}

interface SpeechRecognitionErrorEvent extends Event {
  readonly error: string;
  readonly message: string;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

type SpeechRecognitionCtor = new () => SpeechRecognition;

/**
 * The push-to-talk key. macOS `fn` emits no browser KeyboardEvent (it is
 * consumed at the firmware/OS layer), so the Wispr-Flow-style fn-hold UX is
 * delivered via an OS-level `hidutil` remap of fn → Right Control; the web app
 * binds the remap target. Binding the RIGHT control specifically means
 * left-Ctrl chords (Ctrl+A/E/K readline, Windows AltGr which synthesizes
 * ControlLeft) can never arm PTT by construction.
 */
const PTT_KEY_CODE = 'ControlRight';

/**
 * Hold shorter than this is treated as an accidental tap, not a PTT intent —
 * recording (and the mic-permission prompt on first use) starts only after the
 * key has been down this long.
 */
const ARM_DELAY_MS = 250;

/**
 * After release, the engine gets this long to flush pending finals and end
 * gracefully. If it never does (engine hang — observed in the wild), commit
 * what was heard and hard-abort so the machine can't wedge in `finalizing`.
 */
const FINALIZE_WATCHDOG_MS = 4000;

/**
 * Chrome ends `continuous` sessions early (network hiccups, silence detection
 * server-side); while the press is still live we transparently restart. The
 * cap bounds a pathological immediate-end loop.
 */
const MAX_SESSION_RESTARTS = 20;

/**
 * idle → arming (ControlRight down, arm delay pending)
 *      → recording (delay elapsed with key still held; mic live)
 *      → finalizing (released; graceful stop() flushing finals)
 *      → idle (finals committed into the textarea)
 * Pointer-hold and tap-toggle sessions enter at `recording` directly (a
 * deliberate press needs no accidental-tap filter).
 */
type PttState = 'idle' | 'arming' | 'recording' | 'finalizing';

/** What initiated the live session — decides which release path may end it. */
type SessionMode = 'key' | 'pointer' | 'toggle';

interface UseVoiceInputOptions {
  setNewMessage: (updater: (prev: string) => string) => void;
  /**
   * Gates the ControlRight keyboard listeners, and — because the MicButton is
   * disabled alongside the input, leaving no stop affordance — commits and
   * ends any live pointer/toggle session when it flips false. Combined
   * internally with the `global` hotkey scope so keyboard PTT obeys the same
   * overlay arbitration as every other global shortcut.
   */
  enabled?: boolean;
}

interface UseVoiceInputResult {
  isRecording: boolean;
  /**
   * Release-flush in flight (`finalizing`): already-final segments were
   * committed at the release instant, but stragglers finalized mid-utterance
   * may still land via onend (watchdog-bounded ≤4s). Consumers gate Send/Enter
   * on this so the composer can't be sent while its content is unsettled.
   */
  isFinalizing: boolean;
  isSupported: boolean;
  /** Mic-button click: start a session, or finish the live one. */
  toggleRecording: () => void;
  /** Mic-button pointer-down: begin a hold session immediately (no arm delay). */
  pressStart: () => void;
  /** Mic-button pointer-up after a hold: finish and commit. */
  pressEnd: () => void;
  /** Abort the live session and DISCARD the buffer (Esc). No-op when idle. */
  cancel: () => void;
}

export function useVoiceInput({
  setNewMessage,
  enabled = true,
}: UseVoiceInputOptions): UseVoiceInputResult {
  const [isRecording, setIsRecording] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);

  const recognitionCtor: SpeechRecognitionCtor | undefined =
    typeof window !== 'undefined'
      ? (
          window as unknown as {
            SpeechRecognition?: SpeechRecognitionCtor;
            webkitSpeechRecognition?: SpeechRecognitionCtor;
          }
        ).SpeechRecognition ||
        (
          window as unknown as {
            webkitSpeechRecognition?: SpeechRecognitionCtor;
          }
        ).webkitSpeechRecognition
      : undefined;
  const isSupported = !!recognitionCtor;

  // Keyboard PTT obeys the same overlay arbitration as `global`-scope
  // shortcuts: HotkeyScopeSync (the single writer, KEYBOARD_CONTRACT §2)
  // disables `global` while any modal/drawer/help overlay is open.
  const { activeScopes } = useHotkeysContext();
  const keyboardActive =
    enabled && isSupported && activeScopes.includes(HOTKEY_SCOPES.global);

  // Latest-ref pattern (KEYBOARD_CONTRACT §5.2b, same exemption as
  // useActionHotkey): the raw listeners bind once per enable-flip and read the
  // freshest setter through the ref, never a stale closure.
  const setNewMessageRef = useRef(setNewMessage);
  // eslint-disable-next-line react-hooks/refs
  setNewMessageRef.current = setNewMessage;

  const stateRef = useRef<PttState>('idle');
  const modeRef = useRef<SessionMode>('toggle');
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  /** Final transcript segments accumulated across the live session. */
  const bufferRef = useRef('');
  /** ControlRight is physically down. */
  const heldRef = useRef(false);
  /**
   * Chord latch: once ControlRight participates in a chord (or Esc aborted a
   * held session), PTT stays suppressed until the key comes back up — the OS
   * auto-repeat and the chord's remaining keystrokes must not re-arm it.
   */
  const suppressedRef = useRef(false);
  const armTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const watchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const restartsRef = useRef(0);

  const setState = (next: PttState) => {
    stateRef.current = next;
    setIsRecording(next === 'recording');
    setIsFinalizing(next === 'finalizing');
  };

  const clearTimer = (timer: {
    current: ReturnType<typeof setTimeout> | null;
  }) => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  };

  const detachRecognition = () => {
    const rec = recognitionRef.current;
    if (!rec) return;
    rec.onresult = null;
    rec.onerror = null;
    rec.onend = null;
    try {
      rec.abort();
    } catch {
      // abort() throws if the engine is not currently active
    }
    recognitionRef.current = null;
  };

  const commitBuffer = () => {
    const text = bufferRef.current.trim();
    bufferRef.current = '';
    if (!text) return;
    // Release inserts the text — never auto-sends (ENG-4572).
    setNewMessageRef.current(prev => (prev ? prev + ' ' + text : text));
  };

  /** Tear the session down; `commit` inserts what was heard, else discards. */
  const endSession = (commit: boolean) => {
    clearTimer(armTimerRef);
    clearTimer(watchdogRef);
    detachRecognition();
    if (commit) {
      commitBuffer();
    } else {
      bufferRef.current = '';
    }
    setState('idle');
  };

  const startSession = (mode: SessionMode) => {
    if (!recognitionCtor) return;
    if (stateRef.current === 'recording' || stateRef.current === 'finalizing')
      return;
    clearTimer(armTimerRef);

    const rec = new recognitionCtor();
    // continuous defeats the endpointer: pausing or talking slowly must not
    // end the capture — only releasing does.
    rec.continuous = true;
    // Finals only: nothing streams into the textarea mid-hold (ENG-4572
    // adjudication A); the commit happens once, on release.
    rec.interimResults = false;
    rec.lang = 'en-US';

    rec.onresult = (event: SpeechRecognitionEvent) => {
      // Rolling result list: only [resultIndex..length) is new this event, and
      // only isFinal segments are stable — reading results[0] alone (the old
      // bug) drops everything after the first utterance.
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          const segment = result[0].transcript.trim();
          if (segment) {
            bufferRef.current += (bufferRef.current ? ' ' : '') + segment;
          }
        }
      }
    };

    rec.onerror = (event: SpeechRecognitionErrorEvent) => {
      // 'aborted' is self-inflicted (our abort()); 'no-speech' is a benign
      // idle-mic end — onend owns the lifecycle for both.
      if (event.error === 'aborted' || event.error === 'no-speech') return;
      if (
        event.error === 'not-allowed' ||
        event.error === 'service-not-allowed'
      ) {
        console.warn('Speech recognition permission denied:', event.error);
        endSession(false);
        return;
      }
      if (event.error === 'audio-capture') {
        // Mic unplugged mid-dictation: fatal for this session — without this
        // trap the paired onend would churn the bounded restart loop against
        // a device that is gone. Commit the buffered finals (speech is never
        // discarded).
        console.warn('Speech recognition error:', event.error);
        endSession(true);
        return;
      }
      // 'network' is deliberately NOT trapped: transient network ends flow
      // through onend's bounded restart path.
      console.warn('Speech recognition error:', event.error);
    };

    rec.onend = () => {
      if (stateRef.current === 'finalizing') {
        // Graceful stop() flush complete — insert everything heard.
        endSession(true);
        return;
      }
      if (stateRef.current === 'recording' && recognitionRef.current === rec) {
        // Engine ended while the press is still live — restart under the cap.
        if (restartsRef.current < MAX_SESSION_RESTARTS) {
          restartsRef.current += 1;
          try {
            rec.start();
            return;
          } catch {
            // engine refused the restart — fall through and end the session
          }
        }
        endSession(true);
      }
    };

    bufferRef.current = '';
    restartsRef.current = 0;
    recognitionRef.current = rec;
    modeRef.current = mode;
    try {
      rec.start();
      setState('recording');
    } catch {
      endSession(false);
    }
  };

  /** Release path: graceful stop, watchdog-bounded, then commit on onend. */
  const finishSession = () => {
    if (stateRef.current !== 'recording') return;
    const rec = recognitionRef.current;
    setState('finalizing');
    // Early commit: segments already final at the release instant insert
    // synchronously — this closes the common release-after-pause send race
    // (the composer otherwise presented idle while the flush was pending, so
    // Enter/Send could ship it WITHOUT the dictated tail). onend/the watchdog
    // then commit only stragglers finalized mid-utterance — safe because
    // commitBuffer empties the buffer on each commit, is a no-op when empty,
    // and appends preserve spoken order. The isFinalizing gate consumers read
    // covers that straggler window.
    commitBuffer();
    clearTimer(watchdogRef);
    watchdogRef.current = setTimeout(
      () => endSession(true),
      FINALIZE_WATCHDOG_MS
    );
    try {
      rec?.stop();
    } catch {
      endSession(true);
    }
  };

  const cancel = () => {
    if (stateRef.current === 'idle') return;
    endSession(false);
  };

  const toggleRecording = () => {
    if (!isSupported) {
      alert(
        'Speech recognition is not supported in your browser. Please use Chrome, Safari, or Edge.'
      );
      return;
    }
    if (stateRef.current === 'idle') {
      startSession('toggle');
    } else if (stateRef.current === 'recording') {
      finishSession();
    } else if (stateRef.current === 'finalizing') {
      // A fresh click during the release-flush is a new dictation intent: the
      // new utterance wins. Commit what the flush has delivered and start over.
      endSession(true);
      startSession('toggle');
    }
    // arming: a session is already starting — ignore.
  };

  const pressStart = () => {
    if (!isSupported) return;
    // A fresh press during the release-flush is a new dictation intent: the
    // new utterance wins. Commit what the flush has delivered, then the idle
    // check below starts the new session (endSession lands on idle).
    if (stateRef.current === 'finalizing') endSession(true);
    if (stateRef.current === 'idle') startSession('pointer');
  };

  const pressEnd = () => {
    if (stateRef.current === 'recording') finishSession();
  };

  // Scope-gated effect: ControlRight press/release + chord handling ONLY.
  // Esc abort and blur/visibility commit live in the mount-once effect below —
  // they must stay reachable for pointer/toggle sessions while an overlay has
  // this effect unbound (keyboardActive false).
  useEffect(() => {
    if (!keyboardActive) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.isComposing) return;

      // Esc is owned by the mount-once abort listener below — never treat it
      // as a chord key here.
      if (event.key === 'Escape') return;

      if (event.code === PTT_KEY_CODE) {
        if (event.repeat) return; // OS auto-repeat while held
        heldRef.current = true;
        if (suppressedRef.current) return;
        if (stateRef.current === 'finalizing') {
          // A fresh press during the release-flush is a new dictation intent:
          // the new utterance wins. Commit what the flush has delivered
          // (stragglers not yet delivered are forfeited — strictly better than
          // dropping the whole new utterance) and fall through to the normal
          // arm path.
          endSession(true);
        } else if (stateRef.current !== 'idle') {
          return; // arming, or a live pointer/toggle session
        }
        if (event.altKey || event.metaKey || event.shiftKey) {
          // ControlRight arriving with other modifiers down is a chord, not
          // a PTT intent.
          suppressedRef.current = true;
          return;
        }
        setState('arming');
        armTimerRef.current = setTimeout(() => {
          armTimerRef.current = null;
          if (stateRef.current === 'arming' && heldRef.current) {
            startSession('key');
          }
        }, ARM_DELAY_MS);
        return;
      }

      // Any other key while ControlRight is held → chord intent (Ctrl+A etc.):
      // never record through a chord; discard and latch until keyup.
      if (
        heldRef.current &&
        (stateRef.current === 'arming' ||
          (stateRef.current === 'recording' && modeRef.current === 'key'))
      ) {
        suppressedRef.current = true;
        endSession(false);
      }
    };

    const onKeyUp = (event: KeyboardEvent) => {
      if (event.code !== PTT_KEY_CODE) return;
      heldRef.current = false;
      suppressedRef.current = false;
      if (stateRef.current === 'arming') {
        // Released inside the arm delay — an accidental tap, nothing recorded.
        clearTimer(armTimerRef);
        setState('idle');
        return;
      }
      if (stateRef.current === 'recording' && modeRef.current === 'key') {
        finishSession();
      }
    };

    // Raw window listeners, capture phase — the sanctioned escape hatch for a
    // modifier-hold with a paired keyup, which react-hotkeys-hook v5 cannot
    // model (KEYBOARD_CONTRACT §8 step 2).
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('keyup', onKeyUp, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('keyup', onKeyUp, true);
      // Scope-flip (overlay opened / input disabled) with a live key session:
      // the keyup can no longer reach us, so commit rather than wedge.
      // Key-mode only — pointer/toggle sessions keep their stop affordances
      // and survive an overlay-open scope flip.
      heldRef.current = false;
      suppressedRef.current = false;
      if (stateRef.current === 'arming') {
        clearTimer(armTimerRef);
        setState('idle');
      } else if (stateRef.current !== 'idle' && modeRef.current === 'key') {
        endSession(true);
      }
    };
    // startSession/endSession/finishSession operate purely on refs (+ the
    // stable setIsRecording), so these closures can never go stale.
  }, [keyboardActive]);

  // Input surface disabled mid-session: the MicButton is disabled with it, so
  // no stop affordance remains for pointer/toggle sessions — commit and end
  // whatever is live (speech is never discarded). Deliberately keyed on
  // `enabled`, not keyboardActive: an overlay-open scope flip leaves the mic
  // button clickable, so pointer/toggle sessions survive it.
  useEffect(() => {
    if (enabled) return;
    heldRef.current = false;
    suppressedRef.current = false;
    if (stateRef.current === 'arming') {
      clearTimer(armTimerRef);
      setState('idle');
    } else if (stateRef.current !== 'idle') {
      endSession(true);
    }
  }, [enabled]);

  // Mount-once (gated only on browser support): Esc abort + blur/visibility
  // commit for ANY live session. These must not sit under the keyboardActive
  // gate — a pointer/toggle session stays live while an overlay is open
  // (global scope off), and it must still be Esc-abortable and must still
  // commit on focus/tab loss.
  useEffect(() => {
    if (!isSupported) return;

    // Esc aborts a live capture and swallows the event (capture phase) so
    // overlay Esc owners don't also fire (KEYBOARD_CONTRACT §5.6 single-Esc-
    // owner). Deliberately inert when idle — Esc passes through untouched.
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.isComposing) return;
      if (event.key !== 'Escape') return;
      if (stateRef.current === 'idle') return;
      event.preventDefault();
      event.stopPropagation();
      // If the PTT key is still down, it must come back up before re-arming.
      suppressedRef.current = heldRef.current;
      endSession(false);
    };

    // Focus/visibility loss: the paired keyup may never arrive. Commit what
    // was heard — speech is never silently discarded on blur (ENG-4572
    // adjudication B) — and reset the key latches.
    const onBlurOrHide = () => {
      heldRef.current = false;
      suppressedRef.current = false;
      if (stateRef.current === 'arming') {
        clearTimer(armTimerRef);
        setState('idle');
        return;
      }
      if (stateRef.current !== 'idle') endSession(true);
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') onBlurOrHide();
    };

    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('blur', onBlurOrHide);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('blur', onBlurOrHide);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
    // endSession operates purely on refs (+ the stable setIsRecording and the
    // latest-ref setter), so this closure can never go stale.
  }, [isSupported]);

  // Unmount: tear down whatever session survives (pointer/toggle included).
  useEffect(() => {
    return () => {
      clearTimer(armTimerRef);
      clearTimer(watchdogRef);
      detachRecognition();
    };
  }, []);

  return {
    isRecording,
    isFinalizing,
    isSupported,
    toggleRecording,
    pressStart,
    pressEnd,
    cancel,
  };
}
